import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  Server, Bell, FileText, Settings, Users, Puzzle, Handshake,
  Terminal, Store, Shield, Sun, Moon, Menu, X, User, LogOut, KeyRound, ChevronDown
} from 'lucide-react';
import { useAuth } from '../../providers/AuthProvider';
import { useTheme } from '../../providers/ThemeProvider';
import { clsx } from 'clsx';

const navItems = [
  { path: '/clients', label: 'Clients', icon: Server, perm: 'clients' },
  { path: '/analytics', label: 'Analytics', icon: Bell, perm: 'analytics' },
  { path: '/logs', label: 'Logs', icon: FileText, perm: 'logs' },
  { path: '/settings', label: 'Settings', icon: Settings, perm: 'settings' },
];

const adminItems = [
  { path: '/users', label: 'Users', icon: Users, perm: 'users' },
  { path: '/integrations', label: 'Integrations', icon: Puzzle, perm: 'integrations' },
  { path: '/partners', label: 'Partners', icon: Handshake, perm: 'partners' },
  { path: '/proxy', label: 'API Proxy', icon: Terminal, perm: 'proxy' },
  { path: '/vendors', label: 'Vendors', icon: Store, perm: 'vendors' },
  { path: '/roles', label: 'Roles', icon: Shield, perm: null, adminOnly: true },
];

export function AdminLayout() {
  const { user, hasPerm, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userDropdown, setUserDropdown] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const linkClass = ({ isActive }: { isActive: boolean }) => clsx(
    'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors',
    isActive
      ? 'bg-white/15 text-white'
      : 'text-white/70 hover:text-white hover:bg-white/10'
  );

  const visibleNav = navItems.filter(n => hasPerm(n.perm));
  const visibleAdmin = adminItems.filter(n =>
    n.adminOnly ? user?.role === 'admin' : hasPerm(n.perm!)
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0a0a0a]">
      {/* Header */}
      <nav className="bg-gradient-to-r from-[#5b6abf] to-[#6b52a3] dark:bg-[#111] dark:from-[#111] dark:to-[#111] dark:border-b dark:border-gray-800 text-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center py-2">
            {/* Brand */}
            <NavLink to="/clients" className="flex items-center hover:opacity-90 transition-opacity">
              <img
                src="https://www.restroworks.com/wp-content/uploads/2025/04/Restroworks-White.png"
                alt="Connect"
                className="h-[28px]"
              />
              <span className="ml-2 text-lg font-semibold tracking-wide text-white/90">Connect</span>
            </NavLink>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-1">
              {visibleNav.map(item => (
                <NavLink key={item.path} to={item.path} className={linkClass}>
                  <item.icon size={14} />
                  {item.label}
                </NavLink>
              ))}

              {visibleAdmin.length > 0 && (
                <>
                  <div className="w-px h-4 bg-white/20 mx-1" />
                  {visibleAdmin.map(item => (
                    <NavLink key={item.path} to={item.path} className={linkClass}>
                      <item.icon size={14} />
                      {item.label}
                    </NavLink>
                  ))}
                </>
              )}

              <div className="w-px h-4 bg-white/20 mx-1" />

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                title="Toggle dark mode"
              >
                {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
              </button>

              {/* User dropdown */}
              <div className="relative">
                <button
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-white/90 hover:bg-white/10 transition-colors"
                  onClick={() => setUserDropdown(!userDropdown)}
                  onBlur={() => setTimeout(() => setUserDropdown(false), 150)}
                >
                  <User size={14} />
                  {user?.username}
                  <ChevronDown size={12} />
                </button>
                {userDropdown && (
                  <div className="absolute right-0 mt-1 w-44 bg-white dark:bg-[#111] rounded-lg shadow-xl border border-gray-100 dark:border-gray-800 py-1 z-50">
                    <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
                      <p className="text-xs font-medium text-gray-800 dark:text-gray-200">{user?.username}</p>
                      <p className="text-xs text-gray-400">{user?.role}</p>
                    </div>
                    <NavLink
                      to="/change-password"
                      className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <KeyRound size={14} className="text-gray-400" />
                      Change Password
                    </NavLink>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-gray-700 transition-colors w-full text-left"
                    >
                      <LogOut size={14} />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile controls */}
            <div className="flex items-center gap-2 md:hidden">
              <button onClick={toggleTheme} className="p-1.5 text-white/70 hover:text-white">
                {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
              </button>
              <button onClick={() => setMobileOpen(!mobileOpen)} className="p-1.5 text-white">
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-white/10 px-4 pb-3">
            <div className="py-2 space-y-0.5">
              {visibleNav.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={linkClass}
                >
                  <item.icon size={16} />
                  {item.label}
                </NavLink>
              ))}
              {visibleAdmin.length > 0 && (
                <>
                  <div className="h-px bg-white/10 my-2" />
                  <p className="text-xs text-white/30 px-3 py-1 uppercase tracking-wider">Admin</p>
                  {visibleAdmin.map(item => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      className={linkClass}
                    >
                      <item.icon size={16} />
                      {item.label}
                    </NavLink>
                  ))}
                </>
              )}
              <div className="h-px bg-white/10 my-2" />
              <NavLink to="/change-password" onClick={() => setMobileOpen(false)} className={linkClass}>
                <KeyRound size={16} />
                Change Password
              </NavLink>
              <button onClick={handleLogout} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-red-300 hover:bg-white/10 rounded-md w-full">
                <LogOut size={16} />
                Logout
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 py-4">
        <Outlet />
      </main>
    </div>
  );
}
